import type { BranchDto } from '@sellokart/shared';
import type { ReportFilters } from '@/features/reports/useReportFilters';

export interface ReportFilterBarProps {
  filters: ReportFilters;
  onChange: (next: Partial<ReportFilters>) => void;
  branches: BranchDto[];
  /** Some report pages (e.g. Dashboard) don't take an explicit date range — they default server-side. */
  hideDateRange?: boolean;
  showDocType?: boolean;
  /**
   * Distinguishes the input `id`s (and the branch select's accessible label)
   * when a page renders more than one `ReportFilterBar` instance (e.g.
   * InventoryReportPage's separate batches/movement sections) — without this,
   * two instances on the same page would share `id="report-filter-branch"`,
   * which is invalid HTML and breaks the `<label for>` association for every
   * instance after the first.
   */
  idPrefix?: string;
  /** Short label describing what this filter bar's section covers, appended to the branch select's accessible name (e.g. "Stock movement branch"). */
  sectionLabel?: string;
}

const DOC_TYPE_OPTIONS: { value: ReportFilters['docType']; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'pos', label: 'POS' },
  { value: 'invoice', label: 'Invoice' },
];

/**
 * The branch dropdown + date-range inputs + doc-type toggle every report page
 * renders once, near its `PageHeader`. Styled with the same `input`/`label`
 * classes as `Form.tsx`'s fields, rather than inventing new form chrome.
 */
export function ReportFilterBar({
  filters,
  onChange,
  branches,
  hideDateRange = false,
  showDocType = false,
  idPrefix = 'report-filter',
  sectionLabel,
}: ReportFilterBarProps) {
  const branchLabel = sectionLabel ? `Branch (${sectionLabel})` : 'Branch';

  return (
    <div className="mb-6 flex flex-wrap items-end gap-4 rounded-lg border border-slate-200 bg-white p-4">
      <div>
        <label className="label" htmlFor={`${idPrefix}-branch`}>
          {branchLabel}
        </label>
        <select
          id={`${idPrefix}-branch`}
          className="input pr-9"
          value={filters.branchId}
          onChange={(event) => onChange({ branchId: event.target.value })}
        >
          <option value="">All branches</option>
          {branches.map((branch) => (
            <option key={branch.id} value={branch.id}>
              {branch.name}
            </option>
          ))}
        </select>
      </div>

      {hideDateRange ? null : (
        <>
          <div>
            <label className="label" htmlFor={`${idPrefix}-from`}>
              From
            </label>
            <input
              id={`${idPrefix}-from`}
              type="date"
              className="input"
              value={filters.from}
              max={filters.to || undefined}
              onChange={(event) => onChange({ from: event.target.value })}
            />
          </div>
          <div>
            <label className="label" htmlFor={`${idPrefix}-to`}>
              To
            </label>
            <input
              id={`${idPrefix}-to`}
              type="date"
              className="input"
              value={filters.to}
              min={filters.from || undefined}
              onChange={(event) => onChange({ to: event.target.value })}
            />
          </div>
          {!filters.from && !filters.to ? (
            <p className="mb-2 self-end text-xs text-slate-500">Defaults to the last 30 days</p>
          ) : null}
        </>
      )}

      {showDocType ? (
        <div>
          <span className="label">Document type</span>
          <div className="flex overflow-hidden rounded-lg border border-slate-300" role="group" aria-label="Document type">
            {DOC_TYPE_OPTIONS.map((option, index) => (
              <button
                key={option.value || 'all'}
                type="button"
                onClick={() => onChange({ docType: option.value })}
                aria-pressed={filters.docType === option.value}
                className={`px-3 py-2 text-sm font-medium transition-colors ${
                  index > 0 ? 'border-l border-slate-300' : ''
                } ${
                  filters.docType === option.value
                    ? 'bg-brand-600/[0.12] text-brand-700'
                    : 'bg-white text-slate-700 hover:bg-slate-100'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
